import { FilterImpl } from "liquidjs/dist/template";
import { DateTime } from "luxon";

import { StrictStringForLiquid } from "..";

const AVAILABLE_FORMATS: Record<string, Intl.DateTimeFormatOptions> = {
  mmddyy: {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  }, // 10/15/25
  month: { month: "long" }, // October
  short_month: { month: "short" }, // Oct
  year: { year: "numeric" }, // 2025
  short_year: { year: "2-digit" }, // 25
  weekday: { weekday: "long" }, // Tuesday
  short_weekday: { weekday: "short" }, // Tues
  day: { day: "numeric" },
};

const AVAILABLE_TIME_ZONES: Record<string, string> = {
  at: "America/Puerto_Rico",
  et: "America/New_York",
  ct: "America/Chicago",
  mt: "America/Chicago",
  pt: "America/Los_Angeles",
  akt: "America/Anchorage",
  hat: "Pacific/Honolulu",
  utc: "Etc/UTC",
};

const TIMESTAMP_MILLISECOND_CUTOFF = 10000000000;

export default function dateFmt(
  this: FilterImpl,
  liquidString: StrictStringForLiquid,
  format: string = "error",
  tz: string = "error"
): string {
  if (liquidString === null) {
    throw new Error(`invalid date: null`);
  }

  if (!Object.keys(AVAILABLE_FORMATS).includes(format.toLocaleLowerCase())) {
    throw new Error(
      `date requires a valid format, one of ${Object.keys(AVAILABLE_FORMATS)
        .map(k => k.toLocaleUpperCase())
        .join(", ")}`
    );
  }

  if (!Object.keys(AVAILABLE_TIME_ZONES).includes(tz.toLocaleLowerCase())) {
    throw new Error(
      `date requires a valid timezone, one of ${Object.keys(
        AVAILABLE_TIME_ZONES
      )
        .map(t => t.toLocaleUpperCase())
        .join(", ")}`
    );
  }

  let givenDateTime: DateTime;

  try {
    // valueOf throws if the value is not a number, so this try is a different
    // version of `if value is a number do this, else treat it as a string`
    givenDateTime =
      liquidString.valueOf() < TIMESTAMP_MILLISECOND_CUTOFF
        ? DateTime.fromSeconds(Number(liquidString.valueOf()))
        : DateTime.fromMillis(Number(liquidString.valueOf()));
  } catch (_) {
    givenDateTime = DateTime.fromISO(liquidString.toString());
  }

  if (givenDateTime.invalidReason) {
    throw new Error(
      `invalid date: ${givenDateTime.invalidReason} - dates must be either ISO8601 formatted or a unix timestamp`
    );
  }

  const formatObj = AVAILABLE_FORMATS[format.toLocaleLowerCase()];

  if (formatObj) {
    return givenDateTime.toLocaleString({
      ...formatObj,
      timeZone: AVAILABLE_TIME_ZONES[tz.toLocaleLowerCase()],
    });
  }

  throw new Error(`unhandled formatting error`);
}
